/**
 * AgencyOS design tokens — the single source of truth (docs/design_system_et_site_web.md §2, §2 bis).
 * Design v2 (D-104): Apple-grade glass. Semantic colours by layer (backgrounds, labels, separators,
 * fills, accent, status), four glass materials, an SF-inspired type scale, a 4 pt grid, continuous
 * corners, layered shadows and spring motion.
 * Exported as CSS variables (website + web), a Tailwind preset / @theme and a React Native theme.
 * No colour may be written anywhere else in the code base (a test enforces it in ui-web).
 */

export type ThemeName = "light" | "dark";

export interface ColorTokens {
  // ---- Legacy roles (kept: hundreds of screens use them; now mapped onto the v2 palette) ----
  /** Accent used as text, icons and links (AA on every background). Alias of `accent`. */
  brand600: string;
  /** Deep brand tone (charts, website hero). Titles now use `label`. */
  brand900: string;
  accentRed: string;
  /** Amber highlight fill (offer badges, stars). Text on it: `onAccent`. */
  accentAmber: string;
  /** Primary text. Alias of `label`. */
  ink: string;
  /** Secondary text. Alias of `labelSecondary`. */
  muted: string;
  /** Opaque raised surface. Alias of `bgElevated`. */
  surface: string;
  /** Quiet opaque surface (hover rows, chips). Alias of `bgGrouped`. */
  surfaceAlt: string;
  success: string;
  danger: string;
  /** Text on a `brand600` fill (legacy buttons). */
  onBrand: string;
  /** Text on amber highlights (offer badges, offline banner). */
  onAccent: string;
  whatsapp: string;
  onWhatsapp: string;
  focus: string;
  /** Opaque hairline (legacy borders). Alias of `separatorOpaque`. */
  line: string;
  /** Page canvas behind the working screens. Alias of `bg`. */
  canvas: string;
  /** Printed documents and their previews: always white paper, whatever the theme. */
  paper: string;
  onPaper: string;

  // ---- v2 backgrounds, by layer ----
  /** Window / page canvas (layer 0). */
  bg: string;
  /** Opaque raised surface: cards without glass, popovers in the opaque fallback (layer 1). */
  bgElevated: string;
  /** Grouped background: inset panels, table headers, segmented tracks. */
  bgGrouped: string;
  /** Layer 2 on top of an elevated surface (nested panels, inputs in dark mode). */
  bgTertiary: string;
  /** Scrim behind sheets and dialogs. */
  scrim: string;

  // ---- v2 labels (text) ----
  label: string;
  labelSecondary: string;
  /** Placeholders, disabled text, meta that is also given another way (≥ 3:1). */
  labelTertiary: string;
  /** Decorative only (dividers glyphs, watermark). Never carries information. */
  labelQuaternary: string;

  // ---- v2 separators and borders ----
  /** Hairline between rows (translucent: adapts to glass). */
  separator: string;
  separatorOpaque: string;
  /** Border of fields and secondary buttons. */
  border: string;
  /** Border on hover / strong emphasis. */
  borderStrong: string;

  // ---- v2 fills (neutral, translucent: controls, hover, pressed) ----
  fill: string;
  fillSecondary: string;
  fillTertiary: string;
  fillQuaternary: string;
  /** Top-edge sheen on solid fills (primary buttons, selected switches). */
  fillHighlight: string;
  /** Raised thumb of segmented controls. */
  controlThumb: string;
  /** Switch and slider knob. */
  knob: string;

  // ---- v2 accent (derived from the agency brand, see deriveAccent) ----
  accent: string;
  accentFill: string;
  accentFillHover: string;
  accentFillPressed: string;
  onAccentFill: string;
  accentTint: string;
  accentTintHover: string;
  focusRing: string;
  /** Soft glow of the agency colour in the wallpaper (v2.1): never behind text at full strength. */
  accentGlow: string;

  // ---- v2 status ----
  successTint: string;
  warning: string;
  warningTint: string;
  dangerFill: string;
  onDangerFill: string;
  dangerTint: string;
  info: string;
  infoTint: string;
}

/** Background of the working screens (§2 bis): base gradient stops and coloured halos. */
export interface BackdropTokens {
  stops: readonly [string, string, string];
  halos: readonly [string, string, string];
}

/**
 * Wallpaper behind the working screens (v2.1): a fixed, static mesh — a base gradient and four soft
 * colour blobs (the first one is the agency colour, `accentGlow`). It gives the glass something to
 * let through, costs nothing when scrolling (one fixed layer, no animation) and never holds text
 * at a strength that breaks AA (tested).
 */
export interface WallpaperBlob {
  color: string;
  /** Centre, in % of the viewport. */
  x: number;
  y: number;
  /** Radii, in vmax. */
  w: number;
  h: number;
}
export interface WallpaperTokens {
  base: readonly [string, string];
  /** Blob 1 uses the accent glow (agency colour); blobs 2–4 are fixed per theme. */
  blobs: readonly [Omit<WallpaperBlob, "color">, WallpaperBlob, WallpaperBlob, WallpaperBlob];
}

export interface GlassLevel {
  /** Surface colour including alpha. */
  bg: string;
  blur: number;
  saturate: number;
}

export type GlassMaterial = "ultraThin" | "thin" | "regular" | "thick";

export interface GlassTokens {
  /** Toolbars over content once scrolled, chips on photos. */
  ultraThin: GlassLevel;
  /** Sidebar, top bar, tab bar. */
  thin: GlassLevel;
  /** Main cards, popovers, menus. */
  regular: GlassLevel;
  /** Sheets, dialogs, command palette. */
  thick: GlassLevel;
  /** Hairline border of every glass surface (0.5–1 px). */
  hairline: string;
  borderTop: string;
  borderBottom: string;
  /** Specular highlight (radial gradient colour on the top third). */
  highlight: string;
  /** Inner reflection, drawn as an inset box-shadow on the top edge. */
  innerHighlight: string;
  shadow: string;
  /** Opaque fallback: slow device, Save-Data, reduced transparency, no backdrop-filter. */
  opaqueBg: string;
}

export interface SoftTokens {
  /** Light shadow, top-left. */
  light: string;
  /** Dark shadow, bottom-right. */
  dark: string;
  /** Face of a soft control. */
  face: string;
  /** Recessed track (toggle, slider). */
  track: string;
}

/** Layered, natural shadows (several soft layers instead of one hard one). */
export interface ShadowTokens {
  /** Controls: buttons, segmented thumb, switch knob. */
  xs: string;
  /** Cards at rest. */
  sm: string;
  /** Cards on hover, sticky bars. */
  md: string;
  /** Popovers, menus, toasts. */
  lg: string;
  /** Sheets, dialogs, command palette. */
  xl: string;
}

export interface Theme {
  name: ThemeName;
  label: { fr: string; en: string };
  color: ColorTokens;
  backdrop: BackdropTokens;
  wallpaper: WallpaperTokens;
  glass: GlassTokens;
  soft: SoftTokens;
  shadow: ShadowTokens;
}

/** THE BRIDGE WORLD's charter blue: the default accent before an agency sets its own. */
export const DEFAULT_BRAND = "#0047AB";

export const crystal: Theme = {
  name: "light",
  label: { fr: "Clair « Crystal »", en: "Light “Crystal”" },
  color: {
    brand600: "#0047AB",
    brand900: "#0B1F4D",
    accentRed: "#C0392B",
    accentAmber: "#F59E0B",
    ink: "#0B1020",
    muted: "#5A6275",
    surface: "#FFFFFF",
    surfaceAlt: "#F0F2F6",
    success: "#11703A",
    danger: "#B8281A",
    onBrand: "#FFFFFF",
    onAccent: "#1A1206",
    whatsapp: "#0E7A3E",
    onWhatsapp: "#FFFFFF",
    focus: "#0047AB",
    line: "#E3E6EC",
    canvas: "#F4F5F9",
    paper: "#FFFFFF",
    onPaper: "#1A1A2E",

    bg: "#F4F5F9",
    bgElevated: "#FFFFFF",
    bgGrouped: "#F0F2F6",
    bgTertiary: "#FFFFFF",
    scrim: "rgba(10, 14, 26, 0.28)",

    label: "#0B1020",
    labelSecondary: "#5A6275",
    labelTertiary: "#858C9C",
    labelQuaternary: "#B7BCC7",

    separator: "rgba(16, 24, 40, 0.09)",
    separatorOpaque: "#E3E6EC",
    border: "rgba(16, 24, 40, 0.14)",
    borderStrong: "rgba(16, 24, 40, 0.24)",

    fill: "rgba(118, 124, 140, 0.20)",
    fillSecondary: "rgba(118, 124, 140, 0.16)",
    fillTertiary: "rgba(118, 124, 140, 0.11)",
    fillQuaternary: "rgba(118, 124, 140, 0.07)",
    fillHighlight: "rgba(255, 255, 255, 0.22)",
    controlThumb: "#FFFFFF",
    knob: "#FFFFFF",

    accent: "#0047AB",
    accentFill: "#0047AB",
    accentFillHover: "#003F99",
    accentFillPressed: "#003580",
    onAccentFill: "#FFFFFF",
    accentTint: "rgba(0, 71, 171, 0.09)",
    accentTintHover: "rgba(0, 71, 171, 0.14)",
    focusRing: "rgba(0, 71, 171, 0.28)",
    accentGlow: "rgba(26, 117, 255, 0.12)",

    successTint: "rgba(17, 112, 58, 0.11)",
    warning: "#8F4F00",
    warningTint: "rgba(245, 158, 11, 0.16)",
    dangerFill: "#B8281A",
    onDangerFill: "#FFFFFF",
    dangerTint: "rgba(184, 40, 26, 0.09)",
    info: "#0B63B8",
    infoTint: "rgba(11, 99, 184, 0.10)",
  },
  backdrop: {
    stops: ["#F5F7FC", "#EEF2FA", "#F7F4EE"],
    halos: ["rgba(0, 71, 171, 0.10)", "rgba(245, 158, 11, 0.09)", "rgba(192, 57, 43, 0.05)"],
  },
  wallpaper: {
    base: ["#F4F6FB", "#EEF1F7"],
    blobs: [
      { x: 6, y: -8, w: 58, h: 44 },
      { color: "rgba(124, 92, 255, 0.09)", x: 98, y: 2, w: 44, h: 34 },
      { color: "rgba(255, 164, 84, 0.10)", x: 92, y: 104, w: 60, h: 42 },
      { color: "rgba(32, 190, 160, 0.08)", x: 24, y: 102, w: 46, h: 32 },
    ],
  },
  glass: {
    ultraThin: { bg: "rgba(255, 255, 255, 0.50)", blur: 12, saturate: 180 },
    thin: { bg: "rgba(250, 251, 253, 0.56)", blur: 24, saturate: 190 },
    regular: { bg: "rgba(255, 255, 255, 0.70)", blur: 28, saturate: 180 },
    thick: { bg: "rgba(255, 255, 255, 0.88)", blur: 40, saturate: 190 },
    hairline: "rgba(16, 24, 40, 0.08)",
    borderTop: "rgba(255, 255, 255, 0.90)",
    borderBottom: "rgba(16, 24, 40, 0.08)",
    highlight: "rgba(255, 255, 255, 0.55)",
    innerHighlight: "rgba(255, 255, 255, 0.85)",
    shadow: "0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px -4px rgba(16, 24, 40, 0.06)",
    opaqueBg: "#FBFBFD",
  },
  soft: { light: "#FFFFFF", dark: "#D5DBE6", face: "#F4F5F9", track: "#E6E9F0" },
  shadow: {
    xs: "0 0.5px 1px rgba(16, 24, 40, 0.10), 0 1px 2px rgba(16, 24, 40, 0.06)",
    sm: "0 0.5px 1px rgba(16, 24, 40, 0.05), 0 2px 5px -1px rgba(16, 24, 40, 0.05), 0 10px 24px -10px rgba(16, 24, 40, 0.12)",
    md: "0 1px 2px rgba(16, 24, 40, 0.05), 0 6px 14px -4px rgba(16, 24, 40, 0.08), 0 20px 40px -14px rgba(16, 24, 40, 0.16)",
    lg: "0 2px 6px rgba(16, 24, 40, 0.06), 0 12px 28px -6px rgba(16, 24, 40, 0.12), 0 28px 56px -16px rgba(16, 24, 40, 0.14)",
    xl: "0 4px 12px rgba(16, 24, 40, 0.08), 0 24px 48px -12px rgba(16, 24, 40, 0.18), 0 48px 96px -24px rgba(16, 24, 40, 0.20)",
  },
};

export const midnight: Theme = {
  name: "dark",
  label: { fr: "Sombre « Midnight »", en: "Dark “Midnight”" },
  color: {
    // Decision D-017: light accent blue for text on night backgrounds; fills use a deeper blue.
    brand600: "#6FA5FF",
    brand900: "#DCE7FF",
    accentRed: "#FF6B5B",
    accentAmber: "#FBBF24",
    ink: "#F3F5FA",
    muted: "#9CA5B8",
    surface: "#0E121D",
    surfaceAlt: "#141926",
    success: "#3FD37E",
    danger: "#FF7A6B",
    onBrand: "#050810",
    onAccent: "#1A1206",
    whatsapp: "#25D366",
    onWhatsapp: "#04130A",
    focus: "#6FA5FF",
    line: "#1D2332",
    canvas: "#05070D",
    paper: "#FFFFFF",
    onPaper: "#1A1A2E",

    bg: "#05070D",
    bgElevated: "#0E121D",
    bgGrouped: "#0A0D16",
    bgTertiary: "#161B29",
    scrim: "rgba(0, 0, 0, 0.55)",

    label: "#F3F5FA",
    labelSecondary: "#9CA5B8",
    labelTertiary: "#6B7489",
    labelQuaternary: "#3E4658",

    separator: "rgba(255, 255, 255, 0.08)",
    separatorOpaque: "#1D2332",
    border: "rgba(255, 255, 255, 0.12)",
    borderStrong: "rgba(255, 255, 255, 0.22)",

    fill: "rgba(128, 138, 166, 0.30)",
    fillSecondary: "rgba(128, 138, 166, 0.24)",
    fillTertiary: "rgba(128, 138, 166, 0.17)",
    fillQuaternary: "rgba(128, 138, 166, 0.11)",
    fillHighlight: "rgba(255, 255, 255, 0.16)",
    controlThumb: "#343B4D",
    knob: "#F3F5FA",

    accent: "#6FA5FF",
    accentFill: "#2767E8",
    accentFillHover: "#2C6CEC",
    accentFillPressed: "#1F59D0",
    onAccentFill: "#FFFFFF",
    accentTint: "rgba(111, 165, 255, 0.14)",
    accentTintHover: "rgba(111, 165, 255, 0.20)",
    focusRing: "rgba(111, 165, 255, 0.40)",
    accentGlow: "rgba(0, 92, 230, 0.22)",

    successTint: "rgba(63, 211, 126, 0.14)",
    warning: "#FFB84D",
    warningTint: "rgba(251, 191, 36, 0.15)",
    dangerFill: "#D7362A",
    onDangerFill: "#FFFFFF",
    dangerTint: "rgba(255, 122, 107, 0.14)",
    info: "#6CB6FF",
    infoTint: "rgba(108, 182, 255, 0.14)",
  },
  backdrop: {
    stops: ["#05070D", "#070A14", "#060810"],
    halos: ["rgba(39, 103, 232, 0.16)", "rgba(192, 57, 43, 0.07)", "rgba(111, 165, 255, 0.06)"],
  },
  wallpaper: {
    base: ["#060810", "#04060B"],
    blobs: [
      { x: 8, y: -10, w: 60, h: 46 },
      { color: "rgba(122, 84, 255, 0.15)", x: 100, y: 0, w: 46, h: 36 },
      { color: "rgba(20, 150, 200, 0.10)", x: 90, y: 106, w: 58, h: 42 },
      { color: "rgba(214, 72, 52, 0.07)", x: 18, y: 104, w: 44, h: 30 },
    ],
  },
  glass: {
    ultraThin: { bg: "rgba(12, 16, 26, 0.46)", blur: 12, saturate: 160 },
    thin: { bg: "rgba(14, 18, 30, 0.52)", blur: 24, saturate: 180 },
    regular: { bg: "rgba(18, 23, 37, 0.64)", blur: 28, saturate: 170 },
    thick: { bg: "rgba(18, 23, 37, 0.86)", blur: 40, saturate: 180 },
    hairline: "rgba(255, 255, 255, 0.08)",
    borderTop: "rgba(255, 255, 255, 0.10)",
    borderBottom: "rgba(255, 255, 255, 0.04)",
    highlight: "rgba(255, 255, 255, 0.06)",
    innerHighlight: "rgba(255, 255, 255, 0.07)",
    shadow: "0 1px 2px rgba(0, 0, 0, 0.40), 0 8px 24px -8px rgba(0, 0, 0, 0.50)",
    opaqueBg: "#10141F",
  },
  soft: { light: "#1B2233", dark: "#020308", face: "#10141F", track: "#070A12" },
  shadow: {
    xs: "0 0.5px 1px rgba(0, 0, 0, 0.50), 0 1px 2px rgba(0, 0, 0, 0.30)",
    sm: "0 1px 2px rgba(0, 0, 0, 0.40), 0 8px 22px -8px rgba(0, 0, 0, 0.60)",
    md: "0 1px 2px rgba(0, 0, 0, 0.40), 0 6px 14px -4px rgba(0, 0, 0, 0.45), 0 16px 36px -12px rgba(0, 0, 0, 0.60)",
    lg: "0 2px 6px rgba(0, 0, 0, 0.45), 0 14px 32px -8px rgba(0, 0, 0, 0.60), 0 32px 64px -16px rgba(0, 0, 0, 0.70)",
    xl: "0 4px 12px rgba(0, 0, 0, 0.50), 0 28px 56px -12px rgba(0, 0, 0, 0.70), 0 56px 112px -24px rgba(0, 0, 0, 0.80)",
  },
};

export const themes: Record<ThemeName, Theme> = { light: crystal, dark: midnight };

/** SF-inspired text roles. Web sizes are tuned for a dense back office, native ones follow iOS. */
export type TextRole =
  | "largeTitle"
  | "title1"
  | "title2"
  | "title3"
  | "headline"
  | "body"
  | "callout"
  | "subheadline"
  | "footnote"
  | "caption";

export interface TextStyle {
  /** px */
  size: number;
  /** px */
  lineHeight: number;
  weight: 400 | 500 | 600 | 700;
  /** em — negative for large titles (tight, like SF Pro Display). */
  tracking: number;
}

const style = (size: number, lineHeight: number, weight: TextStyle["weight"], tracking: number): TextStyle => ({
  size,
  lineHeight,
  weight,
  tracking,
});

export const typography = {
  /** Apple devices get SF Pro; everyone else gets Inter (variable, optical sizes). */
  fontHeading:
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter Variable", "Inter", system-ui, "Segoe UI", Roboto, sans-serif',
  fontBody:
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter Variable", "Inter", system-ui, "Segoe UI", Roboto, sans-serif',
  fontMono: 'ui-monospace, "SF Mono", "Cascadia Mono", "JetBrains Mono", Consolas, monospace',
  /** PDFs keep Arial to stay identical to the contracts. */
  fontDocument: "Arial, Helvetica, sans-serif",
  /** Legacy raw scale (website). */
  scale: [11, 12, 13, 14, 16, 19, 24, 30, 36, 48, 60, 72] as const,
  web: {
    largeTitle: style(30, 36, 700, -0.026),
    title1: style(24, 30, 700, -0.021),
    title2: style(19, 25, 600, -0.016),
    title3: style(16, 22, 600, -0.011),
    headline: style(14, 20, 600, -0.006),
    body: style(14, 21, 400, -0.004),
    callout: style(13, 19, 400, -0.002),
    subheadline: style(13, 18, 500, -0.002),
    footnote: style(12, 16, 400, 0),
    caption: style(11, 14, 500, 0.012),
  } satisfies Record<TextRole, TextStyle>,
  native: {
    largeTitle: style(34, 41, 700, -0.012),
    title1: style(28, 34, 700, -0.01),
    title2: style(22, 28, 700, -0.008),
    title3: style(20, 25, 600, -0.006),
    headline: style(17, 22, 600, -0.004),
    body: style(17, 22, 400, -0.004),
    callout: style(16, 21, 400, -0.003),
    subheadline: style(15, 20, 400, -0.002),
    footnote: style(13, 18, 400, 0),
    caption: style(12, 16, 400, 0),
  } satisfies Record<TextRole, TextStyle>,
} as const;

/** 4 pt grid: space(1) = 4, space(4) = 16… */
export const space = (steps: number): number => steps * 4;
/** Named steps exported to CSS (--ao-space-N) and Tailwind. */
export const SPACE_STEPS = [0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20] as const;

/**
 * Continuous corners 8 / 12 / 16 / 22 / 28 (squircles where the browser supports corner-shape).
 * xs for chips; sm menu items and small controls; md buttons and fields; lg cards; xl sheets and
 * dialogs; xxl hero cards. `sm/md/lg` keep their names for the mobile screens.
 */
export const radius = {
  xs: 6,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 22,
  xxl: 28,
  pill: 999,
  glass: [16, 22, 28] as const,
} as const;

/** Damped springs (stiffness, damping, mass): used as RN configs and turned into CSS linear(). */
export interface Spring {
  stiffness: number;
  damping: number;
  mass: number;
}

export const springs = {
  /** Presses, toggles, small UI: fast, no overshoot. */
  snappy: { stiffness: 420, damping: 36, mass: 1 },
  /** Sheets, menus, cards entering: smooth, barely any overshoot. */
  smooth: { stiffness: 220, damping: 26, mass: 1 },
  /** Delight (success, badges): a small visible bounce. */
  bouncy: { stiffness: 260, damping: 16, mass: 1 },
} as const satisfies Record<string, Spring>;

export const motion = {
  durationInstant: 100,
  durationFast: 160,
  durationBase: 240,
  durationSlow: 400,
  durationPage: 600,
  durationHero: 1200,
  themeTransition: 400,
  backdropCycleSeconds: 60,
  /** Entrance stagger between siblings. */
  stagger: 40,
  easeOutExpo: [0.16, 1, 0.3, 1] as const,
  easeInOut: [0.65, 0, 0.35, 1] as const,
  /** Apple's standard curve (UIKit default ease). */
  easeStandard: [0.25, 0.1, 0.25, 1] as const,
  springDefault: { damping: 18, stiffness: 180 },
  springs,
  pressScale: 0.98,
} as const;

/** Performance guard (§2 bis): never more than 3 blurred glass layers on screen. */
export const MAX_BLURRED_LAYERS = 3;

/** Minimum touch target (§8). */
export const MIN_TOUCH_TARGET = 44;
